import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ContratoItem } from '../../../services/contratoVentaService';
import ContratoVariablesChips from './ContratoVariablesChips';
import {
  CONTRATO_FIELD_DESC,
  type ContratoPlantillaCaret,
} from './contratoPlantillaVariables';

interface ContratoItemsSectionProps {
  items: ContratoItem[];
  onChange: (items: ContratoItem[]) => void;
  onInsertVariable?: (variable: string) => void;
  onTrackPlantillaCaret?: (caret: ContratoPlantillaCaret) => void;
  readOnly?: boolean;
}

const emptyItem = (orden: number): ContratoItem => ({
  orden,
  codigoProducto: '',
  descripcionPlantilla: '',
  cantidad: 1,
  modoPrecio: 'Fijo',
  precioUnitario: 0,
  bonificacion: 0,
  tipoFacturacion: 'Periodica',
  montoTotal: null,
  notas: '',
});

const cellSx = {
  p: '3px 4px',
  border: 0,
  borderBottom: '1px solid',
  borderColor: 'divider',
  verticalAlign: 'middle',
} as const;

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.78rem',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
} as const;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    height: 32,
  },
  '& .MuiOutlinedInput-input': {
    py: '4px',
    px: '6px',
    fontSize: '0.82rem',
    height: 'auto',
    boxSizing: 'border-box',
  },
};

const textInputProps = {
  style: { padding: '4px 6px', fontSize: '0.82rem' },
};

const numInputProps = {
  style: { textAlign: 'right' as const, padding: '4px 6px', fontSize: '0.82rem' },
  min: 0,
};

const selectSx = {
  ...inputSx,
  '& .MuiSelect-select': {
    py: '4px',
    px: '6px',
    fontSize: '0.82rem',
    minHeight: 'unset !important',
    display: 'flex',
    alignItems: 'center',
  },
};

const ContratoItemsSection: React.FC<ContratoItemsSectionProps> = ({
  items,
  onChange,
  onInsertVariable,
  onTrackPlantillaCaret,
  readOnly,
}) => {
  const updateAt = (index: number, patch: Partial<ContratoItem>) => {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const trackDescripcionCaret = (
    index: number,
    el: HTMLInputElement | HTMLTextAreaElement,
  ) => {
    onTrackPlantillaCaret?.({
      field: CONTRATO_FIELD_DESC,
      itemIndex: index,
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
    });
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {!readOnly && onInsertVariable && (
        <Box sx={{ mb: 1 }}>
          <ContratoVariablesChips onInsert={onInsertVariable} />
        </Box>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            pb: 2,
            boxSizing: 'border-box',
          }}
        >
          <Table size="small" sx={{ minWidth: 1060, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ ...headerCellSx, width: 130 }}>Código</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 160, width: '24%' }}>Descripción</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 72 }}>Cant.</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 110 }}>Modo precio</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 100 }}>Precio</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 72 }}>Desc%</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 110 }}>Tipo</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 110, textAlign: 'right' }}>Monto total</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 140 }}>Notas</TableCell>
                {!readOnly && <TableCell sx={{ width: 48 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={readOnly ? 9 : 10}
                    align="center"
                    sx={{ py: 5, color: 'text.disabled', fontSize: '0.9rem' }}
                  >
                    Buscá un artículo arriba para agregarlo al contrato
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        value={item.codigoProducto}
                        onChange={(e) => updateAt(index, { codigoProducto: e.target.value })}
                        disabled={readOnly}
                        fullWidth
                        inputProps={textInputProps}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 160 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.descripcionPlantilla}
                        onChange={(e) => updateAt(index, { descripcionPlantilla: e.target.value })}
                        onSelect={(e) =>
                          trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                        }
                        onClick={(e) =>
                          trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                        }
                        onKeyUp={(e) =>
                          trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                        }
                        onBlur={(e) => trackDescripcionCaret(index, e.target)}
                        disabled={readOnly}
                        inputProps={{
                          ...textInputProps,
                          'data-contrato-field': CONTRATO_FIELD_DESC,
                          'data-item-index': String(index),
                        }}
                        sx={inputSx}
                        title={
                          item.descripcionPlantilla.includes('{')
                            ? 'Incluye variables que se resuelven al facturar'
                            : item.descripcionPlantilla || undefined
                        }
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.cantidad}
                        onChange={(e) => updateAt(index, { cantidad: Number(e.target.value) || 0 })}
                        disabled={readOnly}
                        fullWidth
                        inputProps={numInputProps}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        select
                        size="small"
                        value={item.modoPrecio}
                        onChange={(e) =>
                          updateAt(index, { modoPrecio: e.target.value as ContratoItem['modoPrecio'] })
                        }
                        disabled={readOnly}
                        fullWidth
                        sx={selectSx}
                      >
                        <MenuItem value="Fijo" sx={{ fontSize: '0.82rem' }}>
                          Fijo
                        </MenuItem>
                        <MenuItem value="ListaPrecios" sx={{ fontSize: '0.82rem' }}>
                          Lista
                        </MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.precioUnitario}
                        onChange={(e) =>
                          updateAt(index, { precioUnitario: Number(e.target.value) || 0 })
                        }
                        disabled={readOnly || item.modoPrecio === 'ListaPrecios'}
                        fullWidth
                        inputProps={numInputProps}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.bonificacion}
                        onChange={(e) =>
                          updateAt(index, { bonificacion: Number(e.target.value) || 0 })
                        }
                        disabled={readOnly}
                        fullWidth
                        inputProps={{ ...numInputProps, max: 100 }}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        select
                        size="small"
                        value={item.tipoFacturacion}
                        onChange={(e) =>
                          updateAt(index, {
                            tipoFacturacion: e.target.value as ContratoItem['tipoFacturacion'],
                          })
                        }
                        disabled={readOnly}
                        fullWidth
                        sx={selectSx}
                      >
                        <MenuItem value="Periodica" sx={{ fontSize: '0.82rem' }}>
                          Periódica
                        </MenuItem>
                        <MenuItem value="PorAvance" sx={{ fontSize: '0.82rem' }}>
                          Por avance
                        </MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textAlign: 'right' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.montoTotal ?? ''}
                        onChange={(e) =>
                          updateAt(index, {
                            montoTotal: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        disabled={readOnly || item.tipoFacturacion !== 'PorAvance'}
                        fullWidth
                        inputProps={numInputProps}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.notas ?? ''}
                        onChange={(e) => updateAt(index, { notas: e.target.value })}
                        disabled={readOnly}
                        inputProps={{ ...textInputProps, maxLength: 500 }}
                        sx={inputSx}
                        title={item.notas || undefined}
                      />
                    </TableCell>
                    {!readOnly && (
                      <TableCell sx={cellSx}>
                        <Tooltip title="Quitar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onChange(items.filter((_, i) => i !== index))}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!readOnly && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onChange([...items, emptyItem(items.length + 1)])}
            variant="text"
            sx={{ textTransform: 'none', fontSize: '0.82rem' }}
          >
            Agregar línea vacía
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ContratoItemsSection;
